import i18n from "../i18n";

const TOKEN_KEY = "godo_token";

function apiErrorMessage(key: string): string {
  const devSuffix = import.meta.env.DEV ? "_dev" : "";
  return i18n.t(`errors.${key}${devSuffix}`, { defaultValue: i18n.t(`errors.${key}`) });
}
const PROFILE_KEY = "godo_profile";
export const LOGOUT_REASON_KEY = "godo_logout_reason";

export function getApiBaseUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL?.trim();
  if (base && base.length > 0) return base.replace(/\/$/, "");
  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();
    if (host.endsWith("godo-erp.com")) {
      return "https://api-pro.godo-erp.com";
    }
  }
  return "/api";
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getStoredProfile<T>(): T | null {
  const raw = localStorage.getItem(PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setStoredProfile(profile: unknown): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function clearStoredProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
}

export function clearSession(): void {
  clearStoredToken();
  clearStoredProfile();
  void import("../lib/local-engine")
    .then(({ clearStoredLocalEngineSecret }) => clearStoredLocalEngineSecret())
    .catch(() => undefined);
}

let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip Authorization header (login). */
  skipAuth?: boolean;
}

function buildUrl(path: string): string {
  const base = getApiBaseUrl();
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

/**
 * Global fetch wrapper — attaches JWT, parses JSON, handles 401 → login redirect.
 */
import { ApiClientError } from "./types";

const DEFAULT_TIMEOUT_MS = 25_000;

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, skipAuth, headers, ...rest } = options;

  const reqHeaders = new Headers(headers);
  if (body !== undefined && !reqHeaders.has("Content-Type")) {
    reqHeaders.set("Content-Type", "application/json");
  }

  if (!skipAuth) {
    const token = getStoredToken();
    if (token) {
      reqHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      ...rest,
      headers: reqHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiClientError(apiErrorMessage("api_timeout"), 0, -1, "timeout");
    }
    throw new ApiClientError(apiErrorMessage("api_network"), 0, -1, "network_error");
  } finally {
    clearTimeout(timeoutId);
  }

  let payload: unknown = null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    payload = await response.json();
  } else if (!response.ok) {
    // Non-JSON failure (e.g. an uncaught 500): surface the status and a body snippet.
    let snippet = "";
    try {
      snippet = (await response.text()).replace(/\s+/g, " ").trim().slice(0, 160);
    } catch {
      /* ignore unreadable body */
    }
    const base = i18n.t("errors.request_failed");
    throw new ApiClientError(
      `${base} (HTTP ${response.status})${snippet ? `: ${snippet}` : ""}`,
      response.status,
      -1,
    );
  }

  if (response.status === 401 && !skipAuth) {
    const errBody = payload as { error?: { message?: string; code?: number; reason?: string } } | null;
    const reason = errBody?.error?.reason ?? "unauthorized";
    if (reason === "session_replaced") {
      try {
        sessionStorage.setItem(LOGOUT_REASON_KEY, errBody?.error?.message ?? "session_replaced");
      } catch {
        /* ignore */
      }
    }
    clearSession();
    unauthorizedHandler?.();
    throw new ApiClientError(
      errBody?.error?.message ?? i18n.t("errors.session_expired"),
      401,
      errBody?.error?.code ?? 401,
      reason,
    );
  }

  if (!response.ok) {
    const errBody = payload as {
      error?: {
        message?: string;
        code?: number;
        reason?: string;
        paywall?: unknown;
      } & Record<string, unknown>;
    } | null;
    const extras: Record<string, unknown> = {};
    if (errBody?.error) {
      const e = errBody.error;
      for (const k of Object.keys(e)) {
        if (k !== "message" && k !== "code" && k !== "reason" && k !== "paywall") {
          extras[k] = (e as Record<string, unknown>)[k];
        }
      }
    }
    const loose = payload as { error?: unknown; message?: unknown } | null;
    const fallbackMessage =
      typeof loose?.error === "string"
        ? loose.error
        : typeof loose?.message === "string"
          ? loose.message
          : `Request failed (HTTP ${response.status})`;
    throw new ApiClientError(
      errBody?.error?.message ?? fallbackMessage,
      response.status,
      errBody?.error?.code ?? response.status,
      errBody?.error?.reason,
      errBody?.error?.paywall,
      Object.keys(extras).length > 0 ? extras : undefined,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "GET" }),

  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "POST", body }),

  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "PATCH", body }),

  put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "PUT", body }),

  delete: <T>(path: string, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "DELETE" }),
};
